import { useEffect, useRef, useState } from 'react';
import { useFilterBridge } from '../../context/FilterBridgeContext';
import './DateFilterDropdown.css';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 Days' },
  { id: 'last30', label: 'Last 30 Days' },
  { id: 'custom', label: 'Custom Date' },
];

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 14h3M13 14h3M8 17h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function DateFilterDropdown({ activePage }) {
  const { filters } = useFilterBridge();
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const rootRef = useRef(null);
  const showDateFilter = activePage === 'wh' || activePage === 'stp';
  const dateLoading = Boolean(filters?.loading);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!showDateFilter || !filters?.onDatePreset) return null;

  const activePreset = filters.activeDatePreset || 'today';
  const activeLabel = PRESETS.find((p) => p.id === activePreset)?.label || 'Today';

  const selectPreset = (id) => {
    if (id === 'custom') {
      setShowCustom(true);
      filters.onDatePreset('custom');
      return;
    }
    setShowCustom(false);
    filters.onDatePreset(id);
    setOpen(false);
  };

  return (
    <div className="date-filter" ref={rootRef}>
      <button
        type="button"
        className={`menu__icon-btn menu__action-btn date-filter__trigger${
          dateLoading ? ' date-filter__trigger--loading' : ''
        }`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Date filter: ${activeLabel}`}
        title={activeLabel}
        aria-busy={dateLoading}
      >
        {dateLoading ? <span className="date-filter__trigger-spinner" aria-hidden /> : <CalendarIcon />}
      </button>

      {open && (
        <div className="date-filter__panel" role="listbox" aria-label="Date range">
          {!showCustom ? (
            <ul className="date-filter__list">
              {PRESETS.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={activePreset === p.id}
                    className={`date-filter__option${activePreset === p.id ? ' date-filter__option--active' : ''}`}
                    onClick={() => selectPreset(p.id)}
                  >
                    <span>{p.label}</span>
                    <ChevronIcon />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="date-filter__custom">
              <p className="date-filter__custom-title">Custom Date</p>
              <div className="date-filter__custom-row">
                <label>
                  From
                  <input
                    type="date"
                    value={filters.draftFrom || ''}
                    max={filters.maxPastDate}
                    onChange={(e) => filters.setDraftFrom?.(e.target.value)}
                  />
                </label>
                <label>
                  To
                  <input
                    type="date"
                    value={filters.draftTo || ''}
                    max={filters.maxPastDate}
                    onChange={(e) => filters.setDraftTo?.(e.target.value)}
                  />
                </label>
              </div>
              <button
                type="button"
                className="date-filter__apply"
                onClick={() => {
                  filters.onApplyHistorical?.();
                  setOpen(false);
                  setShowCustom(false);
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
